import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/navbar/Navbar';
import KonvaDrawing from '../../components/Konva-Drawing/KonvaDrawing';
import './ResultPage.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingAnimation from '../../components/loading-animation/LoadingAnimation';
import { isClientOrgUser as isClientOrgUserFn } from '../../utils/session';


// Motifs de classement « sans suite ». Les clés sont les volets cochés par Veo
// et stockés dans stats.volet (cf. sansSuiteBaseOptions). « other » n'y figure
// pas : il n'a pas de message type, c'est la précision saisie qui fait foi.
const SANS_SUITE_MESSAGES = {
  Constat: "Le constat est absent ou illisible.",
  Image: "Les images du véhicule sont floues ou trop sombres.",
  DifferentVoiture: "Le véhicule photographié ne correspond pas à une voiture.",
  ImageVehiculeErrone: "L'image du véhicule est erronée.",
  DifferentCompagnieAssurance: "La compagnie d'assurance est différente.",
};

const VLM_STATE = {
  conserve: { label: "Conservé", cls: "vlm-state--keep" },
  retire: { label: "Retiré par le VLM", cls: "vlm-state--removed" },
  ajoute: { label: "Ajouté par le VLM", cls: "vlm-state--added" },
  modifie: { label: "Suggestion modifiée", cls: "vlm-state--changed" },
};

/** Bandeau de statut de l'étape VLM d'une photo (bilan, ou raison pour laquelle elle n'a pas joué) */
const VlmImageBanner = ({ vlm }) => {
  if (!vlm) return null;
  const v = vlm.validation || {};
  if (vlm.status === "ok" || vlm.status === "incomplet") {
    const incomplete = vlm.status === "incomplet";
    return (
      <div className={`vlm-banner ${incomplete ? "vlm-banner--warn" : ""}`}>
        <strong>VLM</strong> : {v.confirmes ?? 0} confirmée(s), {v.infirmes ?? 0} écartée(s), {vlm.ajouts ?? 0} ajout(s)
        {incomplete && " — étape incomplète (erreurs côté VLM), résultat partiel"}
      </div>
    );
  }
  const messages = {
    vue_ignoree: "VLM non appliqué à cette vue (vue d'ensemble) — résultat YOLO seul.",
    erreur: "VLM indisponible sur cette photo — résultat YOLO seul.",
    non_configure: "VLM non configuré (clé absente) — résultat YOLO seul.",
    non_applique: "VLM non appliqué à cette photo — résultat YOLO seul.",
  };
  return <div className="vlm-banner vlm-banner--warn">{messages[vlm.status] || vlm.message || `VLM : ${vlm.status}`}</div>;
};

/** Avant (YOLO seul) / après (VLM) par photo, pour comparer avec la validation */
const VlmTracePanel = ({ photos }) => {
  if (!photos || photos.length === 0) return null;
  const count = (etat) => photos.reduce((n, ph) => n + ph.rows.filter((r) => r.etat === etat).length, 0);
  const label = (v) => (!v ? "—" : String(v).toLowerCase().startsWith("remplac") ? "Remplacement" : "Réparation");
  const fmt = (p) => (p ? `${label(p.suggestion)}${p.damage_prob != null ? ` · ${Number(p.damage_prob).toFixed(1)}%` : ""}` : "—");
  return (
    <details className="vlm-trace">
      <summary>
        Traçabilité VLM — avant / après · {count("retire")} retiré(s), {count("ajoute")} ajouté(s), {count("modifie")} suggestion(s) modifiée(s)
      </summary>
      {photos.map((ph) => (
        <div key={ph.title} className="vlm-trace__photo">
          <h4>{ph.title}</h4>
          {ph.rows.length === 0 ? <p className="vlm-trace__empty">Aucun dommage avant ni après.</p> : (
            <table className="vlm-trace__table">
              <thead><tr><th>Pièce</th><th>Dommage</th><th>Avant (YOLO)</th><th>Après (VLM)</th><th>Résultat</th><th>Justification</th></tr></thead>
              <tbody>
                {ph.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{String(r.part || "").replace(/_/g, " ")}</td>
                    <td>{r.type}</td>
                    <td>{fmt(r.avant)}</td>
                    <td>{fmt(r.apres)}</td>
                    <td><span className={`vlm-state ${VLM_STATE[r.etat].cls}`}>{VLM_STATE[r.etat].label}</span></td>
                    <td>{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </details>
  );
};

const ResultPage = () => {

  // ════════════════════════════════════════════════════════════
  // CONSTANTES MÉTIER
  // ════════════════════════════════════════════════════════════

  const partsList = [
    'Capot', 'Pare-choc Arriere', 'Aile Arriere Gauche', 'Aile Arriere Droite',
    'Aile Avant Gauche', 'Aile Avant Droite', 'Feu Arriere Droit', 'Feu Arriere Gauche',
    'Porte Arriere Droite', 'Porte Arriere Gauche', 'Bas de caisse Droit', 'Bas de caisse Gauche',
    'Porte Avant Gauche', 'Porte Avant Droite', 'Phare Gauche', 'Phare Droit', 'Retroviseur Droit',
    'Retroviseur Gauche', 'Coffre', 'Roue Avant', 'Roue Arriere', 'Pare-choc Avant', 'Pare-brise', 'lunette AR',
    'Pavillon', 'Calandre', 'Trappe', 'Cache', 'Enjoliveur aile', 'Antibrouillard G', 'Antibrouillard D',
    'Baguette Porte Avd', 'Baguette Porte Avg', 'Baguette Porte Ard', 'Baguette Porte Arg',
    'Moteur', 'Radiateur', 'Frein', 'Levier de frein', 'Essieu Arrière', 'Renfort Avant', 'Renfort Arrière',
    'Jupe Avant', 'Jupe Arriere', 'Spoiler Avant', 'Spoiler Arrière',
    'Catadioptre Arriere Gauche', 'Catadioptre Arriere Droit',
    'Baguette Aile Avant Gauche', 'Baguette Aile Avant Droite', 'Baguette Aile Arriere Gauche', 'Baguette Aile Arriere Droite',
    'Baguette Malle Arriere', 'Baguette Pare-chocs Avant', 'Baguette Pare-chocs Arriere',
    'Support Pare-chocs Avant', 'Support Pare-chocs Arriere', 'Support Plaque Avant',
    'Plaque', 'Logo', 'Signal', 'Charnier', 'Joint', 'Garniture', 'Habillage',
    'Serrure Capot', 'Serrure Coffre',
    'Cache Optique', 'Cache Antibrouillard Gauche', 'Cache Antibrouillard Droite', 'Cache Retroviseur',
    'Vitre AVD', 'Vitre AVG', 'Vitre ARD', 'Vitre ARG',
    'Poignee AVD', 'Poignee AVG', 'Poignee ARD', 'Poignee ARG',
    'Radar de recul', 'Capteur', 'Capteur Aide Stationnement',
    'Porte Battante Arriere Gauche', 'Porte Battante Arriere Droite',
    'Pare-boue', 'Vis', "Cataphote", "Jante", "Pneu", "Sigle", "MAIN D'OEUVRE PEINTURE", "MAIN D'OEUVRE", "ING. PEINTURE"
  ];
  const damagesList = [
    'Eraflures', 'Enfoncement', 'Corrosion', 'Ecaillement', 'Fissure', 'Rayure',
    'deboitement', 'Absent', 'Corrompu', 'Casse', 'BDG'
  ];
  const suggestionsList = ['Reparation', 'Remplacement'];
  const orientationOptions = [
    { value: "RMA SERVICE", label: "RMA Service" },
    { value: "CONCESSIONNAIRE", label: "Concessionnaire" },
    { value: "GA", label: "Garage agréé (GA)" },
    { value: "EAD", label: "EAD" },
    { value: "HIFAD", label: "HIFAD" },
    { value: "PN", label: "Procédure normale" },
  ];
  const title_translation = {
    "Front Side": "côté avant",
    "Front left side": "côté avant gauche",
    "Rear left side": "côté arrière gauche",
    "Rear Side": "côté arrière",
    "Rear right side": "côté arrière droit",
    "Front right side": "côté avant droit",
    "Step 10 feet back and take a wide shot of the damage": "vue d'ensemble des dégâts en 3 mètres",
    "Take detailed photos of the damage": "photos détaillées des dégâts",
    "Take a close-up of the right side of the damage": "côté droit des dégâts",
    "Take a close-up of the left side of the damage": "côté gauche des dégâts",
  };

  // ════════════════════════════════════════════════════════════
  // STATES
  // ════════════════════════════════════════════════════════════

  const navigate = useNavigate();
  const { code } = useParams();
  const konvaRef = useRef(null);

  // ─── SOURCE UNIQUE DE VÉRITÉ ───
  const [imagesInfo, setImagesInfo] = useState([]);

  // ─── Données indépendantes (backend / formulaire) ───
  const [personalInfo, setPersonalInfo] = useState({});
  const [vehicleInfo, setVehicleInfo] = useState({});
  const [econstatData, setEconstatData] = useState({
    zones_endommagees: [], link_constat: '', dmc: '', taux: '',
    circonstance: '', cas_bareme: '', taux_organisation: '', taux_veo: '',
    cas_bareme_veo: '', ville: ''
  });
  const [econstatFound, setEconstatFound] = useState(true);
  const [Orientation, setOrientation] = useState("");
  // ─── Champs orientation RMA (4 champs) ───
  const [orientationIaVeo, setOrientationIaVeo] = useState("");       // figé (IA ou Veo si dispo)
  const [orientationAgent, setOrientationAgent] = useState("");       // optionnel
  const [orientationProposee, setOrientationProposee] = useState(""); // obligatoire (RMA)
  const [observation, setObservation] = useState("");
  const [status, setStatus] = useState("");
  const [statusRma, setStatusRma] = useState("");
  const [verifiedOrNot, setVerifiedOrNot] = useState(true);
  const [reportTotalPrice, setReportTotalPrice] = useState(null); // null = prix absent du rapport
  const [prixIa, setPrixIa] = useState(null);   // estimation IA (dossier.prix_ia)
  const [prixVeo, setPrixVeo] = useState(null); // prix validé par Veo (dossier.prix_veo)
  const [sansSuiteVolet, setSansSuiteVolet] = useState("");         // stats.volet
  const [sansSuitePrecision, setSansSuitePrecision] = useState(""); // stats.precision
  const [imagesNotProcessed, setImagesNotProcessed] = useState([]);

  // ─── MO/ING éditables manuellement (pas dérivables à 100%) ───
  const [manualMo, setManualMo] = useState(null);  // null = utiliser la valeur dérivée
  const [manualIng, setManualIng] = useState(null); // null = utiliser la valeur dérivée

  // ─── UI states ───
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isViewerFullscreen, setIsViewerFullscreen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [loading, setLoading] = useState(true);

  // ─── Modals ───
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [selectedDamagesToDelete, setSelectedDamagesToDelete] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [rowToUpdate, setRowToUpdate] = useState(null);
  const [selectedDamageToUpdate, setSelectedDamageToUpdate] = useState(null);
  const [updateModalStep, setUpdateModalStep] = useState('selectDamage');
  const [updatedDamageData, setUpdatedDamageData] = useState({
    idToUpdate: '', part_name: '', damage_type: '', damage_prob: '', price: '', suggestion: '',
  });
  const [updatePriceLoading, setUpdatePriceLoading] = useState(false);
  const [partSearch, setPartSearch] = useState("");
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackCategories, setFeedbackCategories] = useState([]);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackMode, setFeedbackMode] = useState("save");
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [showSansSuiteDetails, setShowSansSuiteDetails] = useState(false);
  const [showSaveDetails, setShowSaveDetails] = useState(false);
  const [showConstatDetails, setShowConstatDetails] = useState(false);

  const isClientOrgUser = isClientOrgUserFn();
// ════════════════════════════════════════════════════════════
  // DÉRIVATIONS (useMemo) — tout est calculé depuis imagesInfo
  // ════════════════════════════════════════════════════════════

  /** imagesSummary : tableau résumé groupé par part_name */
  const imagesSummary = useMemo(() => {
    const allParts = [];
    (imagesInfo || []).forEach((img) => {
      (img.damaged_parts || []).forEach((part) => {
        allParts.push({ ...part, imageTitle: img.title });
      });
    });

    // Grouper par part_name (normalisé)
    const grouped = new Map();
    allParts.forEach((part) => {
      const key = String(part.part_name || "").replace(/ /g, "_").toLowerCase();
      if (!key) return;
      if (!grouped.has(key)) {
        grouped.set(key, { title: part.imageTitle, part_name: part.part_name, damages: [] });
      }
      grouped.get(key).damages.push(part);
    });

    return Array.from(grouped.values()).map((group) => {
      const damageTypes = [...new Set(group.damages.map((d) => d.damage_type).filter(Boolean))];
      const damage_probs = {};
      const damage_prices = {};
      const damage_suggestions = {};
      const damage_ids = {};
      let mo_peint_sum = 0;
      let ing_peint_sum = 0;

      // Accumulateurs calqués sur group_and_sum_damages (Backend/utils/ApiUtils.py) :
      // Remplacement et Réparation sont cumulés séparément, car une même pièce peut
      // être détectée plusieurs fois avec des suggestions opposées.
      let hasReplacement = false;
      let bestReplacementPrice = 0;
      const bestRepairPriceByType = {};

      group.damages.forEach((d) => {
        const dt = d.damage_type;
        if (!dt) return;
        const price = Number(d.price) || 0;
        const isReplacement = (d.suggestion || "").toLowerCase() === "remplacement";

        // Garder la prob max
        damage_probs[dt] = Math.max(damage_probs[dt] || 0, Number(d.damage_prob) || 0);
        // Prix affiché par type : le max des occurrences (jamais la dernière rencontrée)
        damage_prices[dt] = Math.max(damage_prices[dt] || 0, price);
        // Un type est en Remplacement dès qu'une seule de ses occurrences l'est
        if (isReplacement) damage_suggestions[dt] = "Remplacement";
        else if (!damage_suggestions[dt]) damage_suggestions[dt] = d.suggestion || "Reparation";
        if (!damage_ids[dt]) damage_ids[dt] = [];
        if (!damage_ids[dt].includes(d.id)) damage_ids[dt].push(d.id);

        if (isReplacement) {
          hasReplacement = true;
          // MO/ING de l'occurrence retenue, pas la somme des occurrences
          if (price > bestReplacementPrice) {
            bestReplacementPrice = price;
            mo_peint_sum = Number(d.mo_peint || 0);
            ing_peint_sum = Number(d.ing_peint || 0);
          }
        } else {
          bestRepairPriceByType[dt] = Math.max(bestRepairPriceByType[dt] || 0, price);
        }
      });

      // Règle prix : Remplacement → prix max des occurrences à remplacer,
      // Réparation → somme des meilleurs prix de réparation par type
      let price;
      if (hasReplacement) {
        price = bestReplacementPrice;
      } else {
        price = Object.values(bestRepairPriceByType).reduce((s, v) => s + (Number(v) || 0), 0);
        mo_peint_sum = 0;
        ing_peint_sum = 0;
      }

      console.log("[imagesSummary] Row price computed:", {
    part_name: group.part_name,
    suggestion: hasReplacement ? "Remplacement" : "Reparation",
    price,
    mo_peint: mo_peint_sum,
    ing_peint: ing_peint_sum,
  });

      return {
        title: group.title,
        part_name: group.part_name,
        damage_type: damageTypes,
        damage_probs,
        damage_prices,
        damage_suggestions,
        damage_ids,
        price,
        suggestion: hasReplacement ? "Remplacement" : "Reparation",
        mo_peint: mo_peint_sum,
        ing_peint: ing_peint_sum,
        isEditing: group.damages.some((d) => d.isEditing),
        // Dommages découverts par le VLM (ligne « vlm » ou seconde zone d'une ligne YOLO)
        vlmTypes: [...new Set(group.damages
          .filter((d) => d.source === "vlm" || d.source === "yolo+vlm")
          .map((d) => d.damage_type).filter(Boolean))],
      };
    });
  }, [imagesInfo]);

  /** Prix totaux dérivés */
  const derivedTotals = useMemo(() => {
    let partsTotal = 0, moSum = 0, ingSum = 0;
    imagesSummary.forEach((row) => {
      partsTotal += Number(row.price) || 0;
      moSum += Number(row.mo_peint) || 0;
      ingSum += Number(row.ing_peint) || 0;
    });
    return { partsTotal, derivedMo: moSum, derivedIng: ingSum };
  }, [imagesSummary]);

  /** Traçabilité VLM : par photo, lignes YOLO seul (`vlm.avant`) comparées au résultat courant */
  const vlmComparison = useMemo(() => {
    const norm = (v) => String(v || "").replace(/ /g, "_").toLowerCase();
    const sug = (v) => (String(v || "").toLowerCase().startsWith("remplac") ? "Remplacement" : "Réparation");
    const photos = [];
    (imagesInfo || []).forEach((img) => {
      const vlm = img.vlm;
      if (!vlm || !Array.isArray(vlm.avant)) return;
      const keyOf = (p) => `${norm(p.part_name)}|${norm(p.damage_type)}`;
      const apres = new Map((img.damaged_parts || []).map((p) => [keyOf(p), p]));
      const avant = new Map(vlm.avant.map((p) => [keyOf(p), p]));
      const rows = [];
      avant.forEach((b, k) => {
        const a = apres.get(k);
        if (!a) {
          const zone = (vlm.rejets || []).find((z) => norm(z.part_name) === norm(b.part_name) && norm(z.damage_type) === norm(b.damage_type));
          rows.push({ part: b.part_name, type: b.damage_type, etat: "retire", avant: b, apres: null,
            note: zone?.justification || zone?.motif || "" });
        } else if (sug(a.suggestion) !== sug(b.suggestion)) {
          rows.push({ part: b.part_name, type: b.damage_type, etat: "modifie", avant: b, apres: a, note: "" });
        } else {
          rows.push({ part: b.part_name, type: b.damage_type, etat: "conserve", avant: b, apres: a,
            note: [
              (a.vlm?.boxes?.length && a.source === "yolo+vlm") ? "zone supplémentaire VLM" : "",
              a.vlm?.zones_ecartees > 0 ? `${a.vlm.zones_ecartees} zone(s) écartée(s) sur ${a.vlm.zones}` : "",
            ].filter(Boolean).join(" · ") });
        }
      });
      apres.forEach((a, k) => {
        if (!avant.has(k)) rows.push({ part: a.part_name, type: a.damage_type, etat: "ajoute", avant: null, apres: a,
          note: a.vlm?.justification || "" });
      });
      photos.push({ title: title_translation[img.title] || img.title, vlm, rows });
    });
    return photos;
  }, [imagesInfo]);

  // MO/ING : valeur manuelle si éditée, sinon dérivée
  const total_mo = manualMo !== null ? manualMo : derivedTotals.derivedMo;
  const total_ing = manualIng !== null ? manualIng : derivedTotals.derivedIng;
  const totalPrice = derivedTotals.partsTotal + total_mo + total_ing;

  // ════════════════════════════════════════════════════════════
  // FEEDBACK OPTIONS (inchangé)
  // ════════════════════════════════════════════════════════════

  const iaFeedbackOptions = [
    { value: "damage_detection", label: "Détection des dommages" },
    { value: "pricing", label: "Estimation du prix / chiffrage" },
    { value: "orientation", label: "Orientation" }
  ];
  const iaFeedbackOtherOption = { value: "other", label: "Autre" };
  const sansSuiteBaseOptions = [
    { value: "Constat", label: "Constat non présent ou illisible" },
    { value: "Image", label: "Images du véhicule floues ou sombres" },
    { value: "DifferentVoiture", label: "Différent de voiture" },
    { value: "ImageVehiculeErrone", label: "Image du véhicule erroné" },
    { value: "DifferentCompagnieAssurance", label: "Différente compagnie d'assurance" }
  ];
  const savePrimaryOption = { value: "dommage_mecanique", label: "Dommage mécanique" };
  const saveMechanicalOptions = [
    { value: "airbag", label: "Dommage airbag" },
    { value: "Dommage moteur", label: "Dommage moteur" },
    { value: "Dommage train avant", label: "Dommage train avant" },
    { value: "Dommage électrique", label: "Dommage électrique" },
    { value: "Dommage transmission", label: "Dommage transmission" },
    { value: "autre", label: "Autre" },
  ];
  const constatPrimaryOption = { value: "constat_data", label: "Récupération des données du constat" };
  const saveConstatOptions = [
    { value: "zones_endommagees", label: "Zone endommagées" },
    { value: "url_constat", label: "Url constat" },
    { value: "responsabilite", label: "Responsabilité" },
    { value: "circonstance", label: "Circonstance" },
  ];

  const toggleFeedbackCategory = (value) => {
    setFeedbackCategories((prev) => {
      const next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      if (value === "dommage_mecanique" && feedbackMode === "save") {
        const willShow = next.includes("dommage_mecanique");
        setShowSaveDetails(willShow);
        if (!willShow) {
          const mechValues = new Set(saveMechanicalOptions.map((o) => o.value));
          return next.filter((v) => !mechValues.has(v));
        }
      }
      if (value === "constat_data" && feedbackMode === "save") {
        const willShow = next.includes("constat_data");
        setShowConstatDetails(willShow);
        if (!willShow) {
          const constatValues = new Set(saveConstatOptions.map((o) => o.value));
          return next.filter((v) => !constatValues.has(v));
        }
      }
      return next;
    });
  };

  const requireComment = false;
  const constatSubValues = new Set(saveConstatOptions.map((o) => o.value));
  const hasConstatSelected = feedbackCategories.includes("constat_data");
  const hasConstatSubSelected = feedbackCategories.some((v) => constatSubValues.has(v));
  const constatValid = !hasConstatSelected || hasConstatSubSelected;
  const mechanicalSubValues = new Set(saveMechanicalOptions.map((o) => o.value));
  const hasMechanicalSelected = feedbackCategories.includes("dommage_mecanique");
  const hasMechanicalSubSelected = feedbackCategories.some((v) => mechanicalSubValues.has(v));
  const mechanicalValid = !hasMechanicalSelected || hasMechanicalSubSelected;
  const isFeedbackValid = feedbackCategories.length > 0 && constatValid && mechanicalValid &&
    (!requireComment || (feedbackComment && feedbackComment.trim().length > 0));

  const toggleDrawingMode = () => setIsDrawing((prev) => !prev);

  const getResponsabilityClass = (taux) => {
    if (!taux) return 'responsibility-none';
    const n = parseFloat(taux.toString().replace('%', '').trim());
    if (isNaN(n)) return 'responsibility-none';
    if (n >= 70) return 'responsibility-high';
    if (n >= 30) return 'responsibility-medium';
    return 'responsibility-low';
  };

  const getPriceRange = (value) => {
    const m = Number(value) || 0;
    const min = Math.floor(m / 1000) * 1000;
    return `[${min} – ${min + 1000} DH]`;
  };

  // ════════════════════════════════════════════════════════════
  // FETCH PRICE ON DEMAND (appelé uniquement depuis les onChange)
  // ════════════════════════════════════════════════════════════

  const fetchPricingEstimate = async (part_name, damage_type, suggestion) => {
    if (!part_name || !damage_type || !suggestion) return;
    setUpdatePriceLoading(true);
    try {
      const params = new URLSearchParams({ part_name, damage_type, suggestion });
      if (vehicleInfo.vin) params.append("vin", vehicleInfo.vin);
      if (vehicleInfo.category) params.append("category", vehicleInfo.category);
      const res = await fetch(`/api/pricing/estimate?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.price !== undefined) {
          setUpdatedDamageData((prev) => ({ ...prev, price: data.price, mo_peint: data.mo_peint || 0, ing_peint: data.ing_peint || 0 }));
        }
      }
    } catch (err) { console.warn("[Pricing] Erreur:", err); }
    finally { setUpdatePriceLoading(false); }
  };

  // ════════════════════════════════════════════════════════════
  // FETCH E-CONSTAT
  // ════════════════════════════════════════════════════════════

  const requestEconstat = (codewp) => fetch(`/api/econstat/${codewp}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
    },
  });

  // `pendingResponse` : requête déjà lancée en parallèle du chargement du dossier.
  const fetchEconstatData = async (codewp, skip = false, dossierStatus = "", dossierStatusRma = "", pendingResponse = null) => {
    try {
      const response = await (pendingResponse || requestEconstat(codewp));
      if (!response.ok) {
        if (response.status === 404) {
          setEconstatFound(false);
          setEconstatData((prev) => ({ ...prev, zones_endommagees: [], link_constat: "", dmc: "", ville: "", taux: "", circonstance: "", cas_bareme: "", taux_organisation: null, taux_veo: null, observation: "", cas_bareme_veo: "" }));
        }
        return;
      }
      const data = await response.json();
      setEconstatFound(true);

      // Priorité du niveau affiché : taux RMA (dossier validé par RMA) >
      // taux Veo (dossier validé par Veo) > taux IA par défaut.
      // status / status_organisation proviennent du dossier (endpoint WeproovCode),
      // car l'API econstat ne les renvoie pas.
      const { taux_organisation, taux_veo, taux } = data;
      let finalTaux;
      if (dossierStatusRma === "validé par organisation") finalTaux = taux_organisation ?? "";
      else if (dossierStatus === "validé") finalTaux = taux_veo ?? "";
      // Sans suite : taux Veo du classement (taux IA pour les anciens dossiers, où il n'était pas enregistré)
      else if (dossierStatus === "sans suite") finalTaux = taux_veo ?? taux ?? "";
      else finalTaux = taux ?? "";

      if (skip) {
        // Dossier non encore validé par Veo (vue RMA) : on masque les bbox/images
        // mais on affiche toujours la circonstance et le niveau de responsabilité.
        setEconstatData((prev) => ({ ...prev, link_constat: data.link_constat || "", dmc: data.dmc || "", ville: data.ville || "", zones_endommagees: [], taux: finalTaux, circonstance: data.circonstance || "", cas_bareme: "", taux_organisation: data.taux_organisation ?? null, taux_veo: null, observation: "", cas_bareme_veo: "" }));
        return;
      }

      let zonesEndommagees = [];
      if (data.zones_endommagees) {
        try { zonesEndommagees = typeof data.zones_endommagees === "string" ? JSON.parse(data.zones_endommagees) : Array.isArray(data.zones_endommagees) ? data.zones_endommagees : []; }
        catch { zonesEndommagees = []; }
      }

      setEconstatData({
        zones_endommagees: zonesEndommagees, link_constat: data.link_constat || "", dmc: data.dmc || "",
        taux: finalTaux, circonstance: data.circonstance || "", status: dossierStatus || "", status_organisation: dossierStatusRma || "",
        cas_bareme: data.cas_bareme || "", taux_veo: data.taux_veo ?? null, taux_organisation: data.taux_organisation ?? null,
        observation: data.observation || "", cas_bareme_veo: data.cas_bareme_veo || "", ville: data.ville || "",
      });
    } catch (error) { console.error("Erreur E-constat:", error); setEconstatFound(false); }
  };

  // ════════════════════════════════════════════════════════════
  // MAIN DATA FETCH
  // ════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!code) return;
    const controller = new AbortController();
    // L'e-constat ne dépend pas du dossier pour être demandé : on le lance en même
    // temps (il n'est interprété qu'une fois le statut du dossier connu).
    const econstatRequest = requestEconstat(code);
    econstatRequest.catch(() => {});
    (async () => {
      try {
        const res = await fetch(`/api/WeproovCode/${code}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
          signal: controller.signal,
        });
        // Dossier inexistant ou d'une autre organisation (le backend répond 404
        // dans les deux cas) : ne pas afficher un formulaire vide et éditable.
        if (res.status === 404) {
          toast.error("Dossier introuvable.");
          navigate("/dashboard", { replace: true });
          return;
        }
        if (!res.ok) throw new Error(`Erreur de chargement (${res.status})`);
        const data = await res.json();

        console.log("[MainDataFetch] Data received from backend:", data);
        
        setPersonalInfo(data.personal_info || {});
        setVehicleInfo(data.vehicle_info || {});

        console.log("[MainDataFetch] Vehicle Info:", data.vehicle_info);
        // ─── Hydrate imagesInfo : chaque damaged_part = 1 dommage ───
        const normalizeSuggestion = (s) => {
          const v = String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
          return v.includes("remplacement") ? "Remplacement" : "Reparation";
        };

        const processedImagesInfo = (data.images_info || []).map((img, index) => {
          // Enrichir les damaged_parts avec les données de images_summary si disponibles
          const summaryMap = new Map();
          (data.images_summary || []).forEach((row) => {
            const key = String(row.part_name || "").replace(/ /g, "_").toLowerCase();
            summaryMap.set(key, row);
          });

          const enrichedParts = (img.damaged_parts || []).map((part) => {
            const partKey = String(part.part_name || "").replace(/ /g, "_").toLowerCase();
            const summaryRow = summaryMap.get(partKey);

            // Déterminer la suggestion par type de dommage
            let suggestion = part.suggestion || "Reparation";
            if (summaryRow?.damage_suggestions) {
              suggestion = summaryRow.damage_suggestions[part.damage_type] || suggestion;
            }
            suggestion = normalizeSuggestion(suggestion);

            return {
              ...part,
              suggestion,
              price: Number(part.price || 0),
              damage_prob: Number(part.damage_prob || 0),
              mo_peint: Number(part.mo_peint || summaryRow?.mo_peint || 0),
              ing_peint: Number(part.ing_peint || summaryRow?.ing_peint || 0),
              isNew: false,
            };
          });

          return { ...img, imageId: String(index), damaged_parts: enrichedParts };
        });

        setImagesInfo(processedImagesInfo);

        // Précharger les images
        processedImagesInfo.forEach((img) => {
          if (img.image_url) { const p = new window.Image(); p.crossOrigin = "Anonymous"; p.src = img.image_url; }
        });

        // MO/ING initiaux depuis le backend
        const mo = Number(data.mo_peint_total ?? 0) || 0;
        const ing = Number(data.ing_peint_total ?? 0) || 0;
        if (mo > 0) setManualMo(mo);
        if (ing > 0) setManualIng(ing);

        // Côté RMA, le save écrit dans orientation_organisation : on relit cette colonne
        // et non data.orientation, qui retombe sur l'IA/Veo tant que le dossier
        // n'est pas "validé par organisation".
        setOrientation((isClientOrgUser ? data.orientation_organisation : data.orientation) || "");
        setOrientationIaVeo(data.orientation_ia_veo || "");
        setOrientationAgent(data.orientation_agent || "");
        setOrientationProposee(data.orientation_proposee || "");
        setObservation(data.observation_organisation || data.observation || "");
        setStatus(data.status || "");
        setStatusRma(data.status_organisation || "");
        setImagesNotProcessed((data.images_not_processed || []).filter((img) => img.image_url));
        setVerifiedOrNot(data?.["verified-or-not"] ?? true);
        setReportTotalPrice(data.total_price == null ? null : Number(data.total_price) || 0);
        setPrixIa(data.prix_ia == null ? null : Number(data.prix_ia) || 0);
        setPrixVeo(data.prix_veo == null ? null : Number(data.prix_veo) || 0);
        setSansSuiteVolet(data.sans_suite_volet || "");
        setSansSuitePrecision(data.sans_suite_precision || "");

        await fetchEconstatData(code, !!data?.is_not_a_valid_dossier, data?.status || "", data?.status_organisation || "", econstatRequest);
      } catch (e) {
        if (e.name === 'AbortError' || e.code === 20) return;
        console.error(e);
      } finally { setLoading(false); }
    })();
    return () => controller.abort();
  }, [code]);

  // ════════════════════════════════════════════════════════════
  // NAVIGATION
  // ════════════════════════════════════════════════════════════

  const goToNext = () => setCurrentIndex((i) => Math.min(i + 1, imagesInfo.length - 1));
  const goToPrevious = () => setCurrentIndex((i) => Math.max(i - 1, 0));

  // Navigation au clavier (← / →) + Échap pour fermer le plein écran
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "Escape") { setIsViewerFullscreen(false); return; }
      if (!imagesInfo.length) return;
      if (e.key === "ArrowRight") { e.preventDefault(); goToNext(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goToPrevious(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesInfo.length]);

  // ════════════════════════════════════════════════════════════
  // 1. AJOUT DOMMAGE DEPUIS L'IMAGE
  // ════════════════════════════════════════════════════════════

  const onNewDamageAdded = (imageId, newDamage) => {
    console.log("[onNewDamageAdded] Price received from KonvaDrawing:", {
    partName: newDamage.partName,
    damageType: newDamage.damageType,
    price: newDamage.price,
    moPeint: newDamage.moPeint,
    ingPeint: newDamage.ingPeint,
  });
    const damageId = `new_${newDamage.partName}_${newDamage.damageType}_${Date.now()}`;

    setImagesInfo((prev) => prev.map((img) => {
      if (img.imageId !== String(imageId)) return img;
      return {
        ...img,
        damaged_parts: [...(img.damaged_parts || []), {
          id: damageId,
          part_name: newDamage.partName,
          damage_type: newDamage.damageType,
          damage_prob: Number(newDamage.damageProb) || 100,
          price: Number(newDamage.price) || 0,
          suggestion: newDamage.suggestion || "Reparation",
          bounding_boxes: [[newDamage.x, newDamage.y, newDamage.x + newDamage.width, newDamage.y + newDamage.height]],
          isNew: true,
          mo_peint: Number(newDamage.moPeint || 0),
          ing_peint: Number(newDamage.ingPeint || 0),
        }],
      };
    }));
    // Summary, prix, boxes → se recalculent automatiquement via useMemo
  };

  // ════════════════════════════════════════════════════════════
  // 2. MODIFICATION DOMMAGE DEPUIS L'IMAGE
  // ════════════════════════════════════════════════════════════

  const onBoxUpdated = (damageId, updatedData) => {
     console.log("[ResultPage] onBoxUpdated called:", {
        damageId,
        receivedPrice: updatedData.price,
        receivedPartName: updatedData.partName,
        receivedDamageType: updatedData.damageType,
      });

    const {
      previousDamageType, previousPartName,
      damageType: newDamageType, price: newPrice,
      suggestion: newSuggestion, partName: newPartName, damageProb: newDamageProb,
    } = updatedData;
    console.log("[ResultPage] onBoxUpdated destructured - newPrice:", newPrice);
    setImagesInfo((prev) => prev.map((img) => ({
      ...img,
      damaged_parts: img.damaged_parts.map((part) => {
        if (part.id !== damageId) return part;
        return {
          ...part,
          part_name: newPartName ?? part.part_name,
          damage_type: newDamageType ?? part.damage_type,
          damage_prob: newDamageProb != null ? Number(newDamageProb) : part.damage_prob,
          price: newPrice != null ? Number(newPrice) : part.price,
          suggestion: newSuggestion ?? part.suggestion,
        };
      }),
    })));
  };

  // ════════════════════════════════════════════════════════════
  // 3. SUPPRESSION DOMMAGE DEPUIS L'IMAGE
  // ════════════════════════════════════════════════════════════

  const onBoxDeleted = (damageId) => {
    setImagesInfo((prev) => prev.map((img) => ({
      ...img,
      damaged_parts: (img.damaged_parts || []).filter((p) => p.id !== damageId),
    })));
  };

  // ════════════════════════════════════════════════════════════
  // 4. MODIFICATION DOMMAGE DEPUIS LE TABLEAU
  // ════════════════════════════════════════════════════════════

  const handleEditClick = (index) => {
    const row = imagesSummary[index];
    setRowToUpdate(index);

    const damageTypes = row.damage_type || [];

    if (damageTypes.length === 1) {
      const singleType = damageTypes[0];
      const idArray = row.damage_ids?.[singleType] || [];

      if (idArray.length === 0) { window.alert(`Aucun ID trouvé pour le dommage "${singleType}".`); return; }

      const theId = idArray[0];

      // Vérifier que le dommage n'apparaît qu'une seule fois
      let totalOcc = 0;
      imagesInfo.forEach((img) => { totalOcc += img.damaged_parts.filter((p) => p.id === theId).length; });
      if (totalOcc === 0) { window.alert(`Aucun dommage trouvé pour l'ID=${theId}.`); return; }
      if (totalOcc > 1) { window.alert(`Impossible de modifier ce dommage: il apparaît ${totalOcc} fois dans les images.`); return; }

      setSelectedDamageToUpdate(singleType);
      setUpdatedDamageData({
        idToUpdate: theId,
        part_name: row.part_name.replace(/_/g, " "),
        damage_type: singleType,
        damage_prob: row.damage_probs?.[singleType] || 0,
        price: row.damage_prices?.[singleType] || 0,
        suggestion: row.damage_suggestions?.[singleType] || row.suggestion || "Reparation",
      });
      setUpdateModalStep("editDamage");
      setShowUpdateModal(true);
    } else {
      setSelectedDamageToUpdate(null);
      setUpdateModalStep("selectDamage");
      setShowUpdateModal(true);
    }
  };

  const confirmDamageSelection = () => {
    if (!selectedDamageToUpdate) { window.alert("Aucun dommage sélectionné."); return; }
    const row = imagesSummary[rowToUpdate];
    const idArray = row.damage_ids?.[selectedDamageToUpdate] || [];
    if (idArray.length === 0) { window.alert(`Aucun ID trouvé pour "${selectedDamageToUpdate}".`); return; }
    const theId = [...new Set(idArray)][0];
    setUpdatedDamageData({
      idToUpdate: theId,
      part_name: row.part_name.replace(/_/g, " "),
      damage_type: selectedDamageToUpdate,
      damage_prob: row.damage_probs?.[selectedDamageToUpdate] || 0,
      price: row.damage_prices?.[selectedDamageToUpdate] || 0,
      suggestion: row.damage_suggestions?.[selectedDamageToUpdate] || row.suggestion || "Reparation",
    });
    setUpdateModalStep("editDamage");
  };

  const handleUpdateDamage = () => {
    if (rowToUpdate === null || !selectedDamageToUpdate) return;
    const { idToUpdate, part_name, damage_type: newDamageType, damage_prob, price, suggestion } = updatedDamageData;
    if (!idToUpdate) { window.alert("Aucun ID trouvé pour la mise à jour."); return; }

    // Vérifier unicité
    let matchCount = 0;
    imagesInfo.forEach((img) => { matchCount += img.damaged_parts.filter((p) => p.id === idToUpdate).length; });
    if (matchCount === 0) { window.alert(`Aucune image ne contient l'ID ${idToUpdate}.`); return; }
    if (matchCount > 1) { window.alert(`Ce dommage apparaît ${matchCount} fois. Modifiez-le directement sur l'image.`); return; }

    // ← Seul setImagesInfo suffit, le summary se recalcule
    setImagesInfo((prev) => prev.map((img) => ({
      ...img,
      damaged_parts: img.damaged_parts.map((part) => {
        if (part.id !== idToUpdate) return part;
        return {
          ...part,
          part_name: part_name,  // espaces OK ici, le summary normalise
          damage_type: newDamageType,
          damage_prob: parseFloat(damage_prob) || 100,
          price: parseFloat(price) || 0,
          suggestion: suggestion || "Reparation",
          mo_peint: (suggestion || "").toLowerCase() === "remplacement" ? Number(updatedDamageData.mo_peint || 0) : 0,
          ing_peint: (suggestion || "").toLowerCase() === "remplacement" ? Number(updatedDamageData.ing_peint || 0) : 0,
        };
      }),
    })));

    setShowUpdateModal(false); setRowToUpdate(null); setSelectedDamageToUpdate(null);
    setUpdatedDamageData({ idToUpdate: '', part_name: '', damage_type: '', damage_prob: '', price: '', suggestion: '' });
    window.alert(`Dommage (ID: ${idToUpdate}) mis à jour avec succès!`);
  };

  const cancelUpdate = () => {
    setShowUpdateModal(false); setRowToUpdate(null); setSelectedDamageToUpdate(null);
    setUpdatedDamageData({ part_name: '', damage_type: '', damage_prob: '', price: '', suggestion: '' });
  };

  // ════════════════════════════════════════════════════════════
  // 5. SUPPRESSION DOMMAGE DEPUIS LE TABLEAU
  // ════════════════════════════════════════════════════════════

  const deleteDamageById = (idToDelete) => {
    // Vérifier unicité
    let totalOcc = 0;
    imagesInfo.forEach((img) => { totalOcc += img.damaged_parts.filter((p) => p.id === idToDelete).length; });
    if (totalOcc > 1) {
      window.alert(`Impossible de supprimer: ce dommage apparaît ${totalOcc} fois dans les images. Supprimez-le directement sur l'image.`);
      return;
    }

    setImagesInfo((prev) => prev.map((img) => ({
      ...img,
      damaged_parts: img.damaged_parts.filter((p) => p.id !== idToDelete),
    })));
  };

  const handleDeleteClick = (index) => {
    const row = imagesSummary[index];
    const allIds = Object.values(row.damage_ids || {}).flat();
    if (allIds.length === 1) { deleteDamageById(allIds[0]); }
    else { setRowToDelete(index); setSelectedDamagesToDelete([]); setShowDeleteModal(true); }
  };

  const deleteSelectedDamages = () => {
    if (rowToDelete === null || selectedDamagesToDelete.length === 0) return;
    selectedDamagesToDelete.forEach((id) => deleteDamageById(id));
    setShowDeleteModal(false); setRowToDelete(null); setSelectedDamagesToDelete([]);
  };

  // ════════════════════════════════════════════════════════════
  // 6. SUPPRESSION D'IMAGE
  // ════════════════════════════════════════════════════════════

  const handleDeleteImage = () => {
    if (isClientOrgUser) return;
    if (!window.confirm("Voulez-vous vraiment supprimer cette image et tous ses dommages ?")) return;

    const deleteIndex = currentIndex;

    setImagesInfo((prev) => {
      const toDelete = prev[deleteIndex];
      // Remettre l'image dans imagesNotProcessed
      if (toDelete?.image_url) {
        setImagesNotProcessed((old) => {
          if ((old || []).some((x) => x.image_url === toDelete.image_url)) return old;
          return [{ title: toDelete.title, image_url: toDelete.image_url, reason: "Supprimée par l'utilisateur" }, ...(old || [])];
        });
      }

      const remaining = prev.filter((_, i) => i !== deleteIndex);
      // Réindexer les imageId
      const reIndexed = remaining.map((img, i) => ({ ...img, imageId: String(i) }));

      // Ajuster currentIndex
      setCurrentIndex((idx) => { const nl = reIndexed.length; if (nl === 0) return 0; return Math.min(idx, nl - 1); });

      return reIndexed;
    });
  };

  // ════════════════════════════════════════════════════════════
  // AJOUT D'IMAGE
  // ════════════════════════════════════════════════════════════

  const addSelectedImage = (selected) => {
    if (!selected) return;
    setImagesInfo((prev) => {
      if (prev.length >= 7) return prev;
      setCurrentIndex(prev.length);
      return [...prev, { title: selected.title, image_url: selected.image_url, damaged_parts: [], imageId: String(prev.length) }];
    });
    setImagesNotProcessed((prev) => prev.filter((img) => img.image_url !== selected.image_url));
    setShowAddImageModal(false);
    toast.success("Image ajoutée.");
  };

  // ════════════════════════════════════════════════════════════
  // SAVE
  // ════════════════════════════════════════════════════════════

  const saveData = async (aiFeedback = null) => {
    setLoading(true);
    let payload;

    if (isClientOrgUser) {
      payload = {
        taux: econstatData.taux_organisation ?? econstatData.taux ?? "",
        observation_organisation: observation ?? "",
        orientation: Orientation || "",
        orientation_agent: orientationAgent || "",
        orientation_proposee: orientationProposee || "",
      };
    } else {
      // Nettoyer les IDs "new_" pour le backend
      const cleanId = (id) => typeof id === 'string' ? id.replace(/^new_/, '') : id;

      const cleanedImages = imagesInfo.map((img) => ({
        ...img,
        damaged_parts: (img.damaged_parts || []).map((part) => ({ ...part, id: cleanId(part.id) })),
      }));

      // Reconstruire images_summary pour le backend (depuis imagesSummary dérivé)
      const cleanedSummary = imagesSummary.map((row) => {
        const cleanedDamageIds = {};
        Object.entries(row.damage_ids || {}).forEach(([type, ids]) => {
          cleanedDamageIds[type] = (ids || []).map(cleanId);
        });
        return { ...row, damage_ids: cleanedDamageIds };
      });

      payload = {
        personal_info: personalInfo, vehicle_info: vehicleInfo,
        images_info: cleanedImages, images_summary: cleanedSummary,
        total_price: totalPrice,
        total_main_oeuvre: total_mo + total_ing || 0,
        taux: econstatData.taux_veo ?? econstatData.taux ?? "",
        observation_organisation: null,
        orientation: Orientation || "",
        ia_feedback: aiFeedback,
        cas_bareme: econstatData.cas_bareme || "",
        totalC_mo: total_mo || 0,
        totalC_ing: total_ing || 0,
      };
    }

    try {
      const response = await fetch(`/api/WeproovCode/save/${code}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
        body: JSON.stringify(payload),
      });
      let responseData;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) responseData = await response.json();
      else responseData = { detail: await response.text() };
      if (!response.ok) throw new Error(responseData?.detail || `Erreur serveur : ${response.statusText}`);

      toast.success("Données sauvegardées avec succès !");
      setFeedbackCategories([]); setFeedbackComment(""); setShowSaveDetails(false); setShowConstatDetails(false);
      navigate("/dashboard");
    } catch (error) {
      console.error('Erreur save:', error);
      toast.error(`Erreur lors de la sauvegarde: ${error.message}`);
    } finally { setLoading(false); }
  };

  const markDossierSansSuite = async (aiFeedback = null) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/dossiers/sans-suite/${code}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("access_token")}` },
        body: JSON.stringify({
          ia_feedback: aiFeedback,
          orientation: Orientation || null,
          taux: String(econstatData.taux_veo ?? econstatData.taux ?? "") || null,
        }),
      });
      if (!response.ok) throw new Error(`Erreur serveur : ${response.statusText}`);
      toast.success("Dossier déclaré sans suite !");
      setFeedbackCategories([]); setFeedbackComment(""); setShowSaveDetails(false); setShowConstatDetails(false);
      navigate("/dashboard");
    } catch (error) {
      console.error("Erreur sans suite:", error);
      toast.error("Erreur lors du passage en sans suite.");
    } finally { setLoading(false); }
  };

  const handleSaveClick = () => {
    console.log("[handleSaveClick] Total Price:", totalPrice, "Orientation:", Orientation);
    if (isClientOrgUser && !orientationProposee) { toast.error("Veuillez sélectionner une orientation proposée avant de sauvegarder."); return; }
    if (!Orientation) { toast.error(isClientOrgUser ? "Veuillez sélectionner une orientation finale avant de sauvegarder." : "Veuillez sélectionner une orientation avant de sauvegarder."); return; }

    // Alerte si prix total = 0 et orientation différente de HIFAD (non applicable côté RMA)
    if (!isClientOrgUser && totalPrice === 0 && Orientation !== "HIFAD") {
      const confirmed = window.confirm(
        "⚠️ Attention : Le prix total est à 0 DH alors que l'orientation choisie n'est pas HIFAD.\n\n" +
        "Voulez-vous quand même continuer la sauvegarde ?"
      );
      if (!confirmed) return;
    }

    if (isClientOrgUser) { saveData(null); }
    else {
      setFeedbackMode("save"); setFeedbackCategories([]); setFeedbackComment("");
      setShowSaveDetails(false); setShowConstatDetails(false); setShowFeedbackModal(true);
    }
  };

  // ════════════════════════════════════════════════════════════
  // AJOUT / ÉDITION INLINE DANS LE TABLEAU (lignes manuelles)
  // ════════════════════════════════════════════════════════════

  const addRow = () => {
    // On ajoute un damaged_part "virtuel" dans la première image (ou crée une entrée spéciale)
    // Note : les lignes ajoutées manuellement via le tableau ne sont pas liées à une image
    // On les stocke dans imagesInfo[0] par convention, avec un flag isManual
    setImagesInfo((prev) => {
      if (prev.length === 0) return prev;
      const targetImg = { ...prev[0] };
      const manualId = `manual_${partsList[0]}_${damagesList[0]}_${Date.now()}`;
      targetImg.damaged_parts = [...(targetImg.damaged_parts || []), {
        id: manualId,
        part_name: partsList[0],
        damage_type: damagesList[0],
        damage_prob: 100,
        price: 0,
        suggestion: suggestionsList[0],
        bounding_boxes: [],
        isNew: true,
        isManual: true,
        isEditing: true,
        mo_peint: 0,
        ing_peint: 0,
      }];
      return prev.map((img, i) => i === 0 ? targetImg : img);
    });
  };

  const editRow = (summaryIndex) => {
    const row = imagesSummary[summaryIndex];
    if (!row) return;
    // Trouver le premier damaged_part correspondant et activer isEditing
    const allIds = Object.values(row.damage_ids || {}).flat();
    if (allIds.length === 0) return;
    setImagesInfo((prev) => prev.map((img) => ({
      ...img,
      damaged_parts: img.damaged_parts.map((p) => 
        allIds.includes(p.id) ? { ...p, isEditing: true } : p
      ),
    })));
  };

  const saveRow = (summaryIndex) => {
    const row = imagesSummary[summaryIndex];
    if (!row) return;
    const allIds = Object.values(row.damage_ids || {}).flat();
    setImagesInfo((prev) => prev.map((img) => ({
      ...img,
      damaged_parts: img.damaged_parts.map((p) => 
        allIds.includes(p.id) ? { ...p, isEditing: false } : p
      ),
    })));
  };

  const handleInputChange = (summaryIndex, field, value) => {
    const row = imagesSummary[summaryIndex];
    if (!row) return;
    const allIds = Object.values(row.damage_ids || {}).flat();
    // Appliquer le changement sur tous les damaged_parts de cette ligne
    setImagesInfo((prev) => prev.map((img) => ({
      ...img,
      damaged_parts: img.damaged_parts.map((p) => {
        if (!allIds.includes(p.id)) return p;
        if (field === 'part_name') return { ...p, part_name: value };
        if (field === 'damage_type') return { ...p, damage_type: Array.isArray(value) ? value[0] : value };
        if (field === 'price') return { ...p, price: Number(value) || 0 };
        if (field === 'suggestion') return { ...p, suggestion: value };
        if (field === 'damage_prob') return { ...p, damage_prob: Number(value) || 0 };
        return p;
      }),
    })));
  };

  const handleAddImage = () => {
    if (!imagesNotProcessed || imagesNotProcessed.length === 0) { toast.info("Aucune image à ajouter."); return; }
    const [nextImage, ...rest] = imagesNotProcessed;
    setImagesInfo((prev) => {
      if (prev.length >= 7) return prev;
      return [...prev, { title: nextImage.title, image_url: nextImage.image_url, damaged_parts: [], imageId: String(prev.length) }];
    });
    setImagesNotProcessed(rest);
    toast.success("Image ajoutée.");
  };

  const displayedTaux = econstatData.taux ?? "";

  // Dossier pas encore traité/validé par VEO, ou classé « sans suite » : côté RMA
  // les dommages sont volontairement non remontés, on masque donc l'indicateur de
  // dommages (afficher « Aucun dommage détecté » serait trompeur).
  const isVeoPending = status === "nouveau" || status === "en cours";
  const isSansSuite = status === "sans suite";
  const showDamageCount = !(isClientOrgUser && (isVeoPending || isSansSuite));

  // Estimation RMA — même cascade que le taux : le prix Veo fait foi dès que le
  // dossier est validé, sinon on montre l'estimation IA. Il n'existe pas de prix
  // RMA (le RMA valide, il ne chiffre pas), d'où deux niveaux au lieu de trois.
  // Le total du rapport sert d'ultime repli (dossiers anciens sans prix en base).
  // Exception : un dossier « sans suite » n'affiche aucun prix.
  const isDossierValide = statusRma === "validé par organisation" || status === "validé";
  const rmaTotalPrice = status === "sans suite"
    ? null
    : isDossierValide
      ? (prixVeo ?? prixIa ?? reportTotalPrice)
      : (prixIa ?? reportTotalPrice);

  // Un dossier « sans suite » a bien été examiné par un expert Veo (il a saisi le
  // motif du rejet) : le badge doit donc le donner pour vérifié, même si ses
  // dommages restent masqués. Seuls « nouveau » et « en cours » sont en attente.
  const isDossierVerifie = verifiedOrNot || isSansSuite;

  // Motifs du classement « sans suite » : stats.volet est une liste de volets
  // séparés par des virgules, chacun traduit en un message lisible.
  const sansSuiteReasons = useMemo(() => {
    const volets = (sansSuiteVolet || "").split(",").map((v) => v.trim()).filter(Boolean);
    const reasons = volets.map((v) => SANS_SUITE_MESSAGES[v]).filter(Boolean);
    if (volets.includes("other") && sansSuitePrecision) reasons.push(sansSuitePrecision);
    return reasons;
  }, [sansSuiteVolet, sansSuitePrecision]);

  // ════════════════════════════════════════════════════════════
  // SUPPRESSION DE TOUS LES DOMMAGES
  // ════════════════════════════════════════════════════════════

  const handleDeleteAllClick = () => {
    if (imagesSummary.length === 0) {
      toast.info("Aucun dommage à supprimer.");
      return;
    }

    if (!window.confirm("Voulez-vous vraiment supprimer TOUS les dommages ? Cette action est irréversible.")) {
      return;
    }

    // Clear all damaged_parts from every image
    setImagesInfo((prev) =>
      prev.map((img) => ({
        ...img,
        damaged_parts: [],
      }))
    );

    // Reset manual MO/ING values
    setManualMo(null);
    setManualIng(null);

    toast.success("Tous les dommages ont été supprimés.");
  };

  // ════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════

  return (
    <div className="assessment-app">
      <br/><br/><br/><br/><br/>
      <Navbar username="Guest" />
      {loading ? (
        <LoadingAnimation />
      ) : (
      <div className="app-container">
        {/* Three Cards in One Row */}
        <div className="info-grid-three">
          {/* Personal Information Card */}
          <div className="info-card personal-card">
            <div className="card-header">
              <span className="card-header-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </span>
              <h2>Informations personnelles</h2>
            </div>
            <div className="card-content">
              <div className="info-row"><span className="label">Prénom:</span><span className="value">{personalInfo.first_name || 'Non disponible'}</span></div>
              <div className="info-row"><span className="label">Nom:</span><span className="value">{personalInfo.last_name || 'Non disponible'}</span></div>
              <div className="info-row"><span className="label">Téléphone:</span><span className="value">{personalInfo.mobile_number || 'Non disponible'}</span></div>
              <div className="info-row"><span className="label">URL Rapport:</span><span className="value">
                {personalInfo.weproov_url ? (<a href={personalInfo.weproov_url} target="_blank" rel="noopener noreferrer" className="constat-link">Voir le rapport</a>) : ('Non disponible')}
              </span></div>

              {isClientOrgUser && (
                <div className="card-footer">
                  <div className="card-footer-row">
                    <span className="label">Statut du dossier</span>
                    <span className={`verify-badge ${isDossierVerifie ? 'verify-badge--ok' : 'verify-badge--pending'}`}>
                      {isDossierVerifie ? (
                        <>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                          Dossier vérifié
                        </>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          Non vérifié
                        </>
                      )}
                    </span>
                  </div>
                  {isSansSuite ? (
                    <p className="ai-disclaimer ai-disclaimer--danger">
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                      <span>
                        <strong>Ce dossier n'a pas pu être traité.</strong>
                        {sansSuiteReasons.length > 0 ? (
                          <>
                            {" Motif :"}
                            <ul className="sans-suite-reasons">
                              {sansSuiteReasons.map((reason, i) => (<li key={i}>{reason}</li>))}
                            </ul>
                          </>
                        ) : (
                          " Aucun motif n'a été renseigné."
                        )}
                      </span>
                    </p>
                  ) : (
                    <p className="ai-disclaimer">
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                      <span>Les résultats sont générés par l'IA à titre indicatif et peuvent contenir des erreurs. Ne vous y fiez pas à 100 % : vérifiez attentivement chaque information et corrigez toute anomalie avant de valider le dossier.</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Vehicle / Dossier Information Card */}
          <div className="info-card dossier-card">
            <div className="card-header">
              <span className="card-header-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>
              </span>
              <h2>Informations Du Dossier</h2>
            </div>
            <div className="card-content">
              <div className="info-row"><span className="label">Immatriculation:</span><span className="value">{vehicleInfo.immatriculation || 'Non disponible'}</span></div>
              <div className="info-row"><span className="label">Marque / modèle: </span><span className="value">{vehicleInfo.marque + ' / ' + vehicleInfo.model || 'Non disponible'}</span></div>
              <div className="info-row"><span className="label">DMC:</span><span className="value">{econstatData.dmc || 'Non disponible'}</span></div>
              <div className="info-row"><span className="label">Ville:</span><span className="value">{econstatData.ville || 'Non disponible'}</span></div>
              {isClientOrgUser ? (
                <div className="orientation-grid">
                  <div className="orientation-cell orientation-cell--ia"><span className="label">Orientation <span className="ia-badge">IA</span></span>
                    <select className="value" value={orientationIaVeo || ""} disabled>
                      <option value="">Non disponible</option>
                      {orientationIaVeo && !orientationOptions.some((opt) => opt.value === orientationIaVeo) && (
                        <option value={orientationIaVeo}>{orientationIaVeo}</option>
                      )}
                      {orientationOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>
                  <div className="orientation-cell"><span className="label">Orientation agent:</span>
                    <select className="value" value={orientationAgent || ""} onChange={(e) => setOrientationAgent(e.target.value)}>
                      <option value="">Sélectionner...</option>
                      {orientationAgent && !orientationOptions.some((opt) => opt.value === orientationAgent) && (
                        <option value={orientationAgent}>{orientationAgent}</option>
                      )}
                      {orientationOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>
                  <div className="orientation-cell"><span className="label">Orientation proposée:</span>
                    <select className="value" value={orientationProposee || ""} onChange={(e) => setOrientationProposee(e.target.value)}>
                      <option value="">Sélectionner...</option>
                      {orientationProposee && !orientationOptions.some((opt) => opt.value === orientationProposee) && (
                        <option value={orientationProposee}>{orientationProposee}</option>
                      )}
                      {orientationOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>
                  <div className="orientation-cell orientation-cell--final"><span className="label">Orientation finale:</span>
                    <select className="value" value={Orientation || ""} onChange={(e) => setOrientation(e.target.value)}>
                      <option value="">Sélectionner...</option>
                      {Orientation && !orientationOptions.some((opt) => opt.value === Orientation) && (
                        <option value={Orientation}>{Orientation}</option>
                      )}
                      {orientationOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="info-row"><span className="label">Orientation:</span>
                  <select className="value" value={Orientation || ""} onChange={(e) => setOrientation(e.target.value)}>
                    <option value="">Sélectionner...</option>
                    {Orientation && !orientationOptions.some((opt) => opt.value === Orientation) && (
                      <option value={Orientation}>{Orientation}</option>
                    )}
                    {orientationOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Responsibility Card */}
          <div className="info-card responsibility-card">
            <div className="card-header">
              <span className="card-header-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </span>
              <h2>Responsabilité</h2>
            </div>
            <div className="card-content">
              <div className="info-row"><span className="label">Niveau responsabilité:</span>
                <select className={`value ${getResponsabilityClass(displayedTaux)}`} value={displayedTaux}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEconstatData((prev) => ({ ...prev, taux: value, ...(isClientOrgUser ? { taux_organisation: value } : { taux_veo: value }) }));
                  }}>
                  <option value="">Sélectionner...</option>
                  <option value="0">Non responsable 0%</option>
                  <option value="50">Partiellement responsable 50%</option>
                  <option value="100">Responsable 100%</option>
                </select>
              </div>
              <div className={`info-row circumstance-row${econstatData.circonstance ? '' : ' circumstance-row--empty'}`}>
                <span className="label">Circonstance:</span>
                {econstatData.circonstance ? (
                  <span className="value circonstance-text">{econstatData.circonstance}</span>
                ) : (
                  <span className="value circonstance-empty">Non disponible</span>
                )}
              </div>
              {isClientOrgUser && (
                <div className="observation-field">
                  <div className="observation-header">
                    <span className="label">Observation:</span>
                    <span className="observation-count">{observation.length}/500</span>
                  </div>
                  <textarea
                    className="observation-textarea"
                    value={observation}
                    onChange={(e) => setObservation(e.target.value)}
                    placeholder="Saisir une observation RMA..."
                    maxLength={500}
                    rows={3}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Image Viewer Section with E-constat Sidebar */}
        <div className="image-viewer-with-sidebar">
          <div className="image-viewer-section">
            <div className="viewer-header">
              <h2>
                <span className="viewer-header-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                </span>
                Inspection visuelle
              </h2>
              <div className="header-actions">
                <div className="viewer-actions">
                  {!isClientOrgUser && imagesInfo.length > 0 && (<button onClick={toggleDrawingMode} className="btn-annotation">{isDrawing ? "Terminer" : "Annoter un Dommage"}</button>)}
                  {!isClientOrgUser && imagesInfo.length < 7 && imagesNotProcessed.length > 0 && (<button onClick={() => setShowAddImageModal(true)} className="btn-primary btn-add-image">Ajouter une image</button>)}
                  {!isClientOrgUser && imagesInfo.length > 0 && (<button type="button" className="btn-danger" disabled={imagesInfo.length === 0} onClick={handleDeleteImage}>🗑️ Supprimer l'image affichée</button>)}
                </div>
                {imagesInfo.length > 0 && (
                  <button type="button" className="btn-fullscreen" onClick={() => setIsViewerFullscreen(true)} aria-label="Agrandir l'image en plein écran" title="Plein écran">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3m13-5v3a2 2 0 0 1-2 2h-3"/></svg>
                  </button>
                )}
                <div className="image-counter">{currentIndex + 1} / {imagesInfo.length}</div>
              </div>
            </div>

            <div className="image-viewer">
              {imagesInfo.length === 0 ? (
                <div className="image-empty-state"><div className="empty-box">
                  <h3>Aucune image disponible</h3><p>Ajoutez une image pour commencer l'inspection.</p>
                  {!isClientOrgUser && imagesNotProcessed.length > 0 && (<button type="button" className="btn-primary" onClick={() => setShowAddImageModal(true)}>➕ Ajouter une image</button>)}
                </div></div>
              ) : (
                <>
                  <button className="nav-btn prev" onClick={goToPrevious} disabled={currentIndex === 0} aria-label="Image précédente">&#x276E;</button>
                  <div className="main-view-container">
                    <div className="image-title-outside">
                      <h3>{title_translation[imagesInfo[currentIndex]?.title] || imagesInfo[currentIndex]?.title || "Sans titre"}</h3>
                      {showDamageCount && (() => {
                        const dmg = imagesInfo[currentIndex]?.damaged_parts?.length || 0;
                        return (
                          <span className={`damage-count ${dmg > 0 ? 'damage-count--has' : 'damage-count--none'}`}>
                            {dmg > 0 ? `${dmg} dommage${dmg > 1 ? 's' : ''} détecté${dmg > 1 ? 's' : ''}` : 'Aucun dommage détecté'}
                          </span>
                        );
                      })()}
                    </div>
                    {!isClientOrgUser && <VlmImageBanner vlm={imagesInfo[currentIndex]?.vlm} />}
                    <div className="main-view">
                      <KonvaDrawing
                        ref={konvaRef}
                        vlmRejected={isClientOrgUser ? [] : (imagesInfo[currentIndex]?.vlm?.rejets || [])}
                        showVlmMarks={!isClientOrgUser}
                        imageUrl={imagesInfo[currentIndex]?.image_url}
                        damagedParts={imagesInfo[currentIndex]?.damaged_parts || []}
                        imageId={currentIndex}
                        imageTitle={imagesInfo[currentIndex]?.title || "Unknown"}
                        onNewDamageAdded={onNewDamageAdded}
                        onBoxUpdated={onBoxUpdated}
                        onBoxDeleted={onBoxDeleted}
                        partsList={partsList.map((part) => part.replace(/_/g, " "))}
                        damagesList={damagesList}
                        suggestionsList={suggestionsList}
                        isDrawing={isDrawing}
                        toggleDrawingMode={toggleDrawingMode}
                        isReadOnly={isClientOrgUser}
                        vin={vehicleInfo.vin || null}
                        category={vehicleInfo.category || null}
                      />
                    </div>
                  </div>
                  <button className="nav-btn next" onClick={goToNext} disabled={currentIndex === imagesInfo.length - 1} aria-label="Image suivante">&#x276F;</button>
                </>
              )}
            </div>
            <div className="thumbnails-container">
              {imagesInfo.map((image, index) => {
                const hasDamage = (image.damaged_parts?.length || 0) > 0;
                const shortTitle = title_translation[image.title] || image.title || `Vue ${index + 1}`;
                return (
                  <button
                    type="button"
                    key={index}
                    className={`thumbnail ${currentIndex === index ? "active" : ""}`}
                    onClick={() => setCurrentIndex(index)}
                    aria-label={`${shortTitle}${hasDamage ? ' — dommage détecté' : ''}`}
                    aria-current={currentIndex === index}
                    title={shortTitle}
                  >
                    <div className="thumbnail-img">
                      <img src={image.image_url} alt={shortTitle} />
                      <span className="thumbnail-badge">{index + 1}</span>
                      {hasDamage && <span className="thumbnail-damage-dot" aria-hidden="true" />}
                    </div>
                    <span className="thumbnail-caption">{shortTitle}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* E-constat Sidebar */}
          <div className="econstat-sidebar">
            <div className="info-card">
              <div className="card-header">
                <h2>Constat</h2>
                {!econstatFound && (<div className="econstat-warning"><span>⚠️ Données E-constat non trouvées</span></div>)}
              </div>
              <div className="card-content">
                {!isClientOrgUser && (
                  <div className="info-row"><span className="label">Zones Endommagées:</span><span className="value">
                    {econstatData.zones_endommagees && econstatData.zones_endommagees.length > 0
                      ? econstatData.zones_endommagees.map((zone, index) => (<div key={index} className="zone-item"><strong>{zone.nom_zone}</strong>{zone.source && <span className="zone-source"> ({zone.source})</span>}</div>))
                      : 'Aucune zone endommagée renseignée'}
                  </span></div>
                )}
                <div className="info-row"><span className="label">URL du constat:</span><span className="value">
                  {econstatData.link_constat ? (<a href={econstatData.link_constat} target="_blank" rel="noopener noreferrer" className="constat-link">Voir le constat</a>) : ('Non disponible')}
                </span></div>
                <div className="info-row"><span className="label">Date de sinistre:</span><span className="value">{vehicleInfo.date_de_sinistre || 'Non disponible'}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Fullscreen image viewer overlay */}
        {isViewerFullscreen && imagesInfo.length > 0 && (
          <div className="viewer-fullscreen" role="dialog" aria-modal="true" aria-label="Inspection visuelle en plein écran" onClick={() => setIsViewerFullscreen(false)}>
            <div className="viewer-fullscreen-inner" onClick={(e) => e.stopPropagation()}>
              <div className="viewer-fullscreen-topbar">
                <span className="viewer-fullscreen-title">
                  {title_translation[imagesInfo[currentIndex]?.title] || imagesInfo[currentIndex]?.title || "Sans titre"}
                  <span className="viewer-fullscreen-counter">{currentIndex + 1} / {imagesInfo.length}</span>
                </span>
                <button type="button" className="viewer-fullscreen-close" onClick={() => setIsViewerFullscreen(false)} aria-label="Fermer le plein écran">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div className="viewer-fullscreen-stage">
                <button className="nav-btn prev" onClick={goToPrevious} disabled={currentIndex === 0} aria-label="Image précédente">&#x276E;</button>
                <div className="viewer-fullscreen-image">
                  <KonvaDrawing
                    key={`fs-${currentIndex}`}
                    vlmRejected={isClientOrgUser ? [] : (imagesInfo[currentIndex]?.vlm?.rejets || [])}
                    showVlmMarks={!isClientOrgUser}
                    imageUrl={imagesInfo[currentIndex]?.image_url}
                    damagedParts={imagesInfo[currentIndex]?.damaged_parts || []}
                    imageId={currentIndex}
                    imageTitle={imagesInfo[currentIndex]?.title || "Unknown"}
                    partsList={partsList.map((part) => part.replace(/_/g, " "))}
                    damagesList={damagesList}
                    suggestionsList={suggestionsList}
                    isDrawing={false}
                    isReadOnly={true}
                    vin={vehicleInfo.vin || null}
                    category={vehicleInfo.category || null}
                  />
                </div>
                <button className="nav-btn next" onClick={goToNext} disabled={currentIndex === imagesInfo.length - 1} aria-label="Image suivante">&#x276F;</button>
              </div>
            </div>
          </div>
        )}

        {/* Damage Summary Table */}
        <div className="summary-section">
          <div className="section-header">
            <h2>Résumé des dommages</h2>
            <div className="header-actions">
              <button className="btn-primary" onClick={handleSaveClick}>Sauvegarder</button>
              {!isClientOrgUser && (
                <button className="btn-danger" onClick={() => {
                  if (!Orientation) { toast.error("Veuillez sélectionner une orientation avant de déclarer sans suite."); return; }
                  setFeedbackMode("sans_suite"); setFeedbackCategories([]); setFeedbackComment("");
                  setShowSansSuiteDetails(false); setShowSaveDetails(false); setShowConstatDetails(false); setShowFeedbackModal(true);
                }}>Déclarer sans suite</button>
              )}
              {!isClientOrgUser && (<button className="btn-secondary" onClick={handleDeleteAllClick}>Supprimer tous les dommages</button>)}
            </div>
          </div>

          <div className="table-container">
            <table className="damage-table">
              <thead><tr>
                <th>Titre</th><th>Nom de la pièce</th><th>Type des dommages</th>
                {!isClientOrgUser && <th>Probabilité des dommages</th>}
                {!isClientOrgUser && <th>Prix (DH)</th>}
                <th>Suggestion</th>
                {!isClientOrgUser && <th>Actions</th>}
              </tr></thead>
              <tbody>
                {imagesSummary.map((item, index) => (
                  <tr key={index} className={item.isEditing ? "editing" : ""}>
                    {item.isEditing ? (
                      <React.Fragment>
                        <td>{title_translation[item.title] || item.title}</td>
                        <td><select value={item.part_name} onChange={(e) => handleInputChange(index, 'part_name', e.target.value)}>{partsList.map((part) => <option key={part} value={part}>{part.replace(/_/g, ' ')}</option>)}</select></td>
                        <td><select value={Array.isArray(item.damage_type) ? item.damage_type[0] : item.damage_type} onChange={(e) => handleInputChange(index, 'damage_type', [e.target.value])}>{damagesList.map((damage) => <option key={damage} value={damage}>{damage}</option>)}</select></td>
                        {!isClientOrgUser && <td>100%</td>}
                        {!isClientOrgUser && <td><input type="number" value={item.price} onChange={(e) => handleInputChange(index, 'price', e.target.value)} /></td>}
                        <td><select value={item.suggestion} onChange={(e) => handleInputChange(index, 'suggestion', e.target.value)}>{suggestionsList.map((s) => <option key={s} value={s}>{s}</option>)}</select></td>
                        {!isClientOrgUser && (<td className="actions"><button className="btn-icon save" onClick={() => saveRow(index)}>✓</button><button className="btn-icon delete" onClick={() => handleDeleteClick(index)}>🗑</button></td>)}
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <td>{title_translation[item.title] || item.title}</td>
                        <td>
                          {String(item.part_name || "").replace(/_/g, ' ')}
                          {!isClientOrgUser && item.vlmTypes?.length > 0 && (
                            <span className="vlm-badge" title={`Ajouté par le VLM : ${item.vlmTypes.join(', ')}`}>VLM</span>
                          )}
                        </td>
                        <td>{Array.isArray(item.damage_type) ? item.damage_type.join(', ') : item.damage_type}</td>
                        {!isClientOrgUser && (<td>
                          {item.damage_probs ? Object.entries(item.damage_probs).map(([dt, prob], i) => (
                            <div key={i} className="probability-item"><span className="damage-type">{dt}</span><span className="probability">{prob}%</span></div>
                          )) : 'N/A'}
                        </td>)}
                        {!isClientOrgUser && (<td className="price">{item.price} DH</td>)}
                        <td><span className={`suggestion ${item.suggestion?.toLowerCase()}`}>{item.suggestion}</span></td>
                        {!isClientOrgUser && (<td className="actions">
                          <button className="btn-icon edit" onClick={() => handleEditClick(index)}>✎</button>
                          <button className="btn-icon delete" onClick={() => handleDeleteClick(index)}>🗑</button>
                        </td>)}
                      </React.Fragment>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {!isClientOrgUser && (
                  <>
                    <tr className="main-oeuvre-row">
                      <td colSpan={4}>Main-d'œuvre (Uniquement pour les pièces à remplacer) : </td>
                      <td className="price">
                        <input type="number" min="0" step="10" value={total_mo}
                          onChange={(e) => setManualMo(Number(e.target.value || 0))}
                          style={{ width: "100%", textAlign: "right", padding: "4px" }} />
                        <span style={{ marginLeft: 6 }}>DH</span>
                      </td>
                      <td></td><td></td>
                    </tr>
                    <tr className="main-oeuvre-row">
                      <td colSpan={4}>Ingrédients peinture (Uniquement pour les pièces à remplacer) : </td>
                      <td className="price">
                        <input type="number" min="0" step="10" value={total_ing}
                          onChange={(e) => setManualIng(Number(e.target.value || 0))}
                          style={{ width: "100%", textAlign: "right", padding: "4px" }} />
                        <span style={{ marginLeft: 6 }}>DH</span>
                      </td>
                      <td></td><td></td>
                    </tr>
                    <tr><td colSpan={4}>Prix Total:</td><td className="price">{totalPrice} DH</td><td></td><td></td></tr>
                  </>
                )}
                {isClientOrgUser && (
                  <tr><td colSpan={3}>Estimation du Prix :</td>
                    <td className="price">{rmaTotalPrice == null ? "Non renseigné" : getPriceRange(rmaTotalPrice)}</td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
          {!isClientOrgUser && <VlmTracePanel photos={vlmComparison} />}
        </div>
      </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && rowToDelete !== null && (
        <div className="modal-overlay"><div className="modal">
          <h3>Supprimer les dommages</h3><p>Veuillez sélectionner les dommages à supprimer :</p>
          {(() => {
            const row = imagesSummary[rowToDelete]; if (!row) return null;
            const uniqueMap = new Map();
            Object.entries(row.damage_ids || {}).forEach(([dt, ids]) => {
              ids.forEach((id) => { if (!uniqueMap.has(id)) uniqueMap.set(id, { id, damageTypes: [dt] }); else uniqueMap.get(id).damageTypes.push(dt); });
            });
            return (<form>{Array.from(uniqueMap.values()).map(({ id, damageTypes }) => (
              <div key={id} className="checkbox-item">
                <input type="checkbox" id={`damage-${id}`} value={id} checked={selectedDamagesToDelete.includes(id)}
                  onChange={(e) => { if (e.target.checked) setSelectedDamagesToDelete((p) => [...p, id]); else setSelectedDamagesToDelete((p) => p.filter((d) => d !== id)); }} />
                <label htmlFor={`damage-${id}`}>{[...new Set(damageTypes)].join(' / ')}</label>
              </div>
            ))}</form>);
          })()}
          <div className="modal-buttons">
            <button className="btn-danger" onClick={deleteSelectedDamages} disabled={selectedDamagesToDelete.length === 0}>Supprimer</button>
            <button className="btn-secondary" onClick={() => { setShowDeleteModal(false); setRowToDelete(null); setSelectedDamagesToDelete([]); }}>Annuler</button>
          </div>
        </div></div>
      )}

      {/* Feedback IA Modal */}
      {showFeedbackModal && !isClientOrgUser && (
        <div className="modal-overlay"><div className="modal">
          <h3>Retour sur l'IA</h3><p>Sur quel volet souhaites-tu signaler une erreur ou une correction par rapport au résultat de l'IA ?</p>
          <form className="modal-form">
            <div className="form-group"><label>Volet(s) concerné(s) :</label>
              <div className="checkbox-list">
                {feedbackMode === "sans_suite" ? (
                  <>
                    {sansSuiteBaseOptions.map((opt) => (<label key={opt.value} className="checkbox-item"><input type="checkbox" value={opt.value} checked={feedbackCategories.includes(opt.value)} onChange={() => toggleFeedbackCategory(opt.value)} />{opt.label}</label>))}
                    <label className="checkbox-item" style={{ marginTop: 10 }}><input type="checkbox" value={iaFeedbackOtherOption.value} checked={feedbackCategories.includes(iaFeedbackOtherOption.value)} onChange={() => toggleFeedbackCategory(iaFeedbackOtherOption.value)} />{iaFeedbackOtherOption.label}</label>
                  </>
                ) : (
                  <>
                    {iaFeedbackOptions.map((opt) => (<label key={opt.value} className="checkbox-item"><input type="checkbox" value={opt.value} checked={feedbackCategories.includes(opt.value)} onChange={() => toggleFeedbackCategory(opt.value)} />{opt.label}</label>))}
                    <label className="checkbox-item" style={{ marginTop: 10 }}><input type="checkbox" value="Image" checked={feedbackCategories.includes("Image")} onChange={() => toggleFeedbackCategory("Image")} />Images du véhicule floues ou sombres</label>
                    <label className="checkbox-item" style={{ marginTop: 10 }}><input type="checkbox" value={constatPrimaryOption.value} checked={feedbackCategories.includes(constatPrimaryOption.value)} onChange={() => toggleFeedbackCategory(constatPrimaryOption.value)} />{constatPrimaryOption.label}</label>
                    {showConstatDetails && (<div style={{ marginLeft: 18, marginTop: 6 }}>
                      {saveConstatOptions.map((opt) => (<label key={opt.value} className="checkbox-item"><input type="checkbox" value={opt.value} checked={feedbackCategories.includes(opt.value)} onChange={() => toggleFeedbackCategory(opt.value)} />{opt.label}</label>))}
                      {!constatValid && (<p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 6, marginBottom: 0 }}>⚠️ Veuillez sélectionner au moins une sous-option</p>)}
                    </div>)}
                    <label className="checkbox-item" style={{ marginTop: 10 }}><input type="checkbox" value={savePrimaryOption.value} checked={feedbackCategories.includes(savePrimaryOption.value)} onChange={() => toggleFeedbackCategory(savePrimaryOption.value)} />{savePrimaryOption.label}</label>
                    {showSaveDetails && (<div style={{ marginLeft: 18, marginTop: 6 }}>
                      {saveMechanicalOptions.map((opt) => (<label key={opt.value} className="checkbox-item"><input type="checkbox" value={opt.value} checked={feedbackCategories.includes(opt.value)} onChange={() => toggleFeedbackCategory(opt.value)} />{opt.label}</label>))}
                      {!mechanicalValid && (<p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 6, marginBottom: 0 }}>⚠️ Veuillez sélectionner au moins une sous-option</p>)}
                    </div>)}
                    <label className="checkbox-item" style={{ marginTop: 10 }}><input type="checkbox" value="all's Good" checked={feedbackCategories.includes("all's Good")} onChange={() => toggleFeedbackCategory("all's Good")} />Tout est bon, merci IA ✨</label>
                    <label className="checkbox-item" style={{ marginTop: 10 }}><input type="checkbox" value={iaFeedbackOtherOption.value} checked={feedbackCategories.includes(iaFeedbackOtherOption.value)} onChange={() => toggleFeedbackCategory(iaFeedbackOtherOption.value)} />{iaFeedbackOtherOption.label}</label>
                  </>
                )}
              </div>
            </div>
            <div className="form-group"><label>Précisions :</label>
              <textarea value={feedbackComment} onChange={(e) => setFeedbackComment(e.target.value)} placeholder="Ex: Dommage non détecté sur l'aile avant droite, ou erreur sur la lecture du constat..." rows={4} />
            </div>
          </form>
          <div className="modal-buttons">
            <button className="btn-primary" disabled={!isFeedbackValid} onClick={() => {
              if (!isFeedbackValid) { toast.error(requireComment ? "Veuillez sélectionner au moins un volet et saisir un commentaire." : "Veuillez sélectionner au moins un volet."); return; }
              const fp = { category: feedbackCategories.join(", "), comment: feedbackComment?.trim() || null };
              setShowFeedbackModal(false);
              if (feedbackMode === "sans_suite") markDossierSansSuite(fp); else saveData(fp);
            }}>{feedbackMode === "sans_suite" ? "Envoyer et déclarer sans suite" : "Envoyer et sauvegarder"}</button>
            <button className="btn-secondary" onClick={() => { setShowFeedbackModal(false); setFeedbackCategories([]); setFeedbackComment(""); setFeedbackMode("save"); setShowSansSuiteDetails(false); setShowSaveDetails(false); setShowConstatDetails(false); }}>Annuler</button>
          </div>
        </div></div>
      )}

      {/* Add Image Modal */}
      {showAddImageModal && (
        <div className="modal-overlay"><div className="modal">
          <h3>Ajouter une image</h3><p>Sélectionnez une image à ajouter.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginTop: "12px" }}>
            {imagesNotProcessed.map((img, idx) => (
              <button key={idx} onClick={() => addSelectedImage(img)} style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "6px", background: "white", cursor: "pointer", textAlign: "left" }}>
                <img src={img.image_url} alt={img.title} style={{ width: "100%", height: "110px", objectFit: "cover", borderRadius: "6px", display: "block" }} />
                <div style={{ marginTop: "6px", fontSize: "13px" }}><strong>{img.title}</strong>{img.reason && (<div style={{ opacity: 0.7, fontSize: "12px" }}>Raison: {img.reason}</div>)}</div>
              </button>
            ))}
          </div>
          <div className="modal-buttons" style={{ marginTop: "14px" }}><button className="btn-primary btn-add-image" onClick={() => setShowAddImageModal(false)}>Annuler</button></div>
        </div></div>
      )}

      {/* Update Modal */}
      {showUpdateModal && rowToUpdate !== null && (
        <div className="modal-overlay"><div className="modal">
          {(() => {
            const row = imagesSummary[rowToUpdate] || {};
            const displayDamageTypes = row.damage_type || [];

            if (updateModalStep === "selectDamage") {
              return (<React.Fragment>
                <h3>Modifier les dommages</h3><p>Veuillez sélectionner le dommage à modifier :</p>
                <form>{displayDamageTypes.map((damage, idx) => (
                  <div key={idx} className="radio-item">
                    <input type="radio" id={`select-damage-${idx}`} name="selectDamage" value={damage} checked={selectedDamageToUpdate === damage} onChange={(e) => setSelectedDamageToUpdate(e.target.value)} />
                    <label htmlFor={`select-damage-${idx}`}>{damage}</label>
                  </div>
                ))}</form>
                <div className="modal-buttons">
                  <button className="btn-primary" onClick={confirmDamageSelection} disabled={!selectedDamageToUpdate}>Suivant</button>
                  <button className="btn-secondary" onClick={cancelUpdate}>Annuler</button>
                </div>
              </React.Fragment>);
            } else if (updateModalStep === "editDamage") {
              return (<React.Fragment>
                <h3>Modifier le dommage</h3>
                <form className="modal-form">
                  <div className="form-group"><label>Nom de la pièce :</label>
                    <input type="text" value={updatedDamageData.part_name} placeholder="Modifier la pièce..."
                      onChange={(e) => { setUpdatedDamageData({ ...updatedDamageData, part_name: e.target.value }); setPartSearch(e.target.value); }} />
                    {partSearch && (<ul className="autocomplete-list">
                      {partsList.filter((p) => p.toLowerCase().startsWith(partSearch.toLowerCase())).slice(0, 8).map((part) => (
                        <li key={part} onClick={() => { setUpdatedDamageData({ ...updatedDamageData, part_name: part }); setPartSearch(""); fetchPricingEstimate(part, updatedDamageData.damage_type, updatedDamageData.suggestion); }}>{part.replace(/_/g, " ")}</li>
                      ))}
                    </ul>)}
                  </div>
                  <div className="form-group"><label htmlFor="damage_type">Type du dommage:</label>
                    <select id="damage_type" value={updatedDamageData.damage_type}
                      onChange={(e) => {
                        const nextType = e.target.value; const r = imagesSummary[rowToUpdate] || {};
                        const nextSuggestion = r.damage_suggestions?.[nextType] ?? r.suggestion ?? updatedDamageData.suggestion;
                        setUpdatedDamageData((prev) => ({ ...prev, damage_type: nextType, damage_prob: r.damage_probs?.[nextType] ?? prev.damage_prob, price: r.damage_prices?.[nextType] ?? prev.price, suggestion: nextSuggestion }));
                        fetchPricingEstimate(updatedDamageData.part_name, nextType, nextSuggestion);
                      }}>
                      {damagesList.map((d) => (<option key={d} value={d}>{d}</option>))}
                    </select>
                  </div>
                  <div className="form-group"><label htmlFor="damage_prob">Probabilité du dommage (%):</label>
                    <input type="number" id="damage_prob" value={updatedDamageData.damage_prob} onChange={(e) => setUpdatedDamageData({ ...updatedDamageData, damage_prob: e.target.value })} min="0" max="100" disabled={updatedDamageData.damage_prob == 100} />
                  </div>
                  <div className="form-group"><label htmlFor="price">Prix (DH):{updatePriceLoading && <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>⏳ calcul...</span>}</label>
                    <input type="number" id="price" value={updatedDamageData.price} onChange={(e) => setUpdatedDamageData({ ...updatedDamageData, price: e.target.value })} min="0" />
                  </div>
                  <div className="form-group"><label htmlFor="suggestion">Suggestion:</label>
                    <select id="suggestion" value={updatedDamageData.suggestion} onChange={(e) => { setUpdatedDamageData({ ...updatedDamageData, suggestion: e.target.value }); fetchPricingEstimate(updatedDamageData.part_name, updatedDamageData.damage_type, e.target.value); }}>
                      {suggestionsList.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </div>
                </form>
                <div className="modal-buttons">
                  <button className="btn-primary" onClick={handleUpdateDamage}>Enregistrer</button>
                  <button className="btn-secondary" onClick={cancelUpdate}>Annuler</button>
                </div>
              </React.Fragment>);
            }
          })()}
        </div></div>
      )}
    </div>
  );
};
export default ResultPage;
